// Path: src/components/permissions/PermissionModal.tsx
import React, { useState, useEffect } from 'react';
import { type School, type User, type PermissionType, getUsers } from '../../lib/api';
import * as api from '../../lib/api';
import { type ResourceContext } from '../../types/permissionTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  schools: School[];
  selectedNode: ResourceContext | null;
  drillDownSchool: School | null;
  editingData: any; // 如果不为 null，表示编辑模式
}

const PermissionModal: React.FC<Props> = ({ 
  isOpen, onClose, onSuccess, schools, selectedNode, drillDownSchool, editingData 
}) => {
  const [schoolId, setSchoolId] = useState('');
  const [userId, setUserId] = useState('');
  const [permType, setPermType] = useState<PermissionType>('view_only');
  const [users, setUsers] = useState<User[]>([]);
  
  // 判断是否为编辑模式 (而非新增)
  const isEdit = !!editingData;
  // 判断是否锁定用户 (即点击了具体某行用户的“修改”按钮)
  const isUserLocked = isEdit && !!editingData.user_id;

  // 初始化表单数据
  useEffect(() => {
    if (isOpen) {
      if (drillDownSchool) {
        // Case A: 在二级列表点击“添加”，锁定学校
        setSchoolId(drillDownSchool.id.toString());
        loadUsers(drillDownSchool.id);
      } 
      
      if (editingData) {
        // Case B: 编辑模式 (可能是批量设置学校，也可能是修改单个用户)
        const sId = editingData.school_id;
        setSchoolId(sId.toString());
        loadUsers(sId);

        if (editingData.user_id) {
          // 锁定特定用户
          setUserId(editingData.user_id.toString());
        } else {
          // 针对全校批量
          setUserId('');
        }
        
        // 回显权限类型
        if (editingData.permission_type) {
          setPermType(editingData.permission_type);
        }
      } else {
        // Case C: 新增模式，重置
        if (!drillDownSchool) {
          setSchoolId('');
          setUserId('');
          setUsers([]);
        }
        setPermType('view_only');
      }
    }
  }, [isOpen, drillDownSchool, editingData]);

  const loadUsers = async (sId: number) => {
    try {
      const res = (await getUsers({ school_id: sId, limit: 1000 })).data;
      setUsers(res);
    } catch (e) { console.error(e); }
  };

  const handleSchoolSelect = (sId: string) => {
    setSchoolId(sId);
    setUserId('');
    if (sId) loadUsers(Number(sId));
    else setUsers([]);
  };

  const handleSubmit = async () => {
    if (!selectedNode || !schoolId) return;
    try {
      const payload: any = { permission_type: permType };
      const sId = Number(schoolId);
      const uId = userId ? Number(userId) : null; // 如果 userId 为空字符串，则为 null

      // ============================================================
      // 核心逻辑分支：根据资源层级 和 目标对象 决定调用哪个 API
      // ============================================================
      
      if (uId) {
        // --- 针对【单个用户】的操作 ---
        payload.user_id = uId;

        if (selectedNode.type === 'course') {
          // 场景 1: 选中 Course，修改单个用户 -> 批量更新该用户在此课程下所有课时权限
          await api.updateCourseUserPermissions(selectedNode.id, payload);
        } else if (selectedNode.type === 'stage') {
          // 场景 1: 选中 Stage，修改单个用户 -> 批量更新该用户在此阶段下所有课时权限
          await api.updateStageUserPermissions(selectedNode.id, payload);
        } else if (selectedNode.type === 'lesson') {
          // 场景 2: 选中 Lesson，修改单个用户 -> 仅更新此课时权限
          await api.updateLessonUserPermissions(selectedNode.id, payload);
        }
      } else {
        // --- 针对【整个学校】的操作 (批量) ---
        payload.school_id = sId;

        if (selectedNode.type === 'course') {
          await api.updateCourseSchoolPermissions(selectedNode.id, payload);
        } else if (selectedNode.type === 'stage') {
          await api.updateStageSchoolPermissions(selectedNode.id, payload);
        } else if (selectedNode.type === 'lesson') {
          await api.updateLessonSchoolPermissions(selectedNode.id, payload);
        }
      }
      
      onSuccess();
    } catch (e) { 
      console.error(e);
      alert("操作失败，请检查网络或控制台日志"); 
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
        
        <div className="bg-white rounded-xl overflow-hidden shadow-2xl transform transition-all sm:max-w-lg sm:w-full z-10 p-6">
          <div className="flex justify-between items-center mb-5 border-b border-gray-100 pb-3">
            <h3 className="text-xl font-bold text-gray-800">
              {isEdit ? '修改权限配置' : '新增权限配置'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>
          
          {/* 提示信息区域 */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-5 text-sm text-blue-800">
            <div className="flex gap-2">
              <span>🎯</span>
              <div>
                <strong>操作对象: </strong> 
                {userId ? (
                  <span>
                    单个用户 
                    {selectedNode?.type !== 'lesson' && <span className="text-xs bg-orange-100 text-orange-700 px-1 ml-1 rounded">批量覆盖子级</span>}
                  </span>
                ) : (
                  <span>全校用户 (批量)</span>
                )}
                <br/>
                <strong>资源范围: </strong> {selectedNode?.type.toUpperCase()} - {selectedNode?.title}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {/* 1. 学校选择 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">所属机构</label>
              <select 
                className="w-full border border-gray-300 p-2.5 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={schoolId}
                onChange={e => handleSchoolSelect(e.target.value)}
                disabled={!!drillDownSchool || isEdit} // 下钻或编辑模式下锁定学校
              >
                <option value="">-- 请选择学校 --</option>
                {schools.filter(s => !s.is_deleted).map(s => (
                  <option key={s.id} value={s.id}>{s.schoolname}</option>
                ))}
              </select>
            </div>

            {/* 2. 用户选择 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                目标用户 
                {!userId && <span className="text-gray-400 font-normal ml-2 text-xs">(未选择则应用到该校所有用户)</span>}
              </label>
              <select 
                className="w-full border border-gray-300 p-2.5 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-500 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                disabled={!schoolId || isUserLocked} // 如果点击了具体的“修改权限”按钮，这里必须锁定
              >
                <option value="">-- 全体用户 (批量应用) --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.username} ({u.role}) {u.memo ? `- ${u.memo}` : ''}</option>
                ))}
              </select>
            </div>

            {/* 3. 权限类型选择 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">权限级别</label>
              <div className="flex gap-4">
                <PermBtn 
                  type="view_only" 
                  current={permType} 
                  onClick={setPermType} 
                  label="👁️ 仅查看" 
                  desc="用户只能浏览内容"
                />
                <PermBtn 
                  type="edit_develop" 
                  current={permType} 
                  onClick={setPermType} 
                  label="✏️ 编辑/开发" 
                  desc="拥有完整的编辑权限"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button 
              onClick={onClose} 
              className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              取消
            </button>
            <button 
              onClick={handleSubmit} 
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-200 font-medium transition-all transform active:scale-95"
            >
              保存配置
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 辅助按钮组件
const PermBtn = ({ type, current, onClick, label, desc }: any) => {
  const isSelected = current === type;
  return (
    <button 
      onClick={() => onClick(type)}
      className={`flex-1 py-3 px-4 rounded-lg border-2 text-left transition-all relative overflow-hidden ${
        isSelected 
          ? 'bg-blue-50 border-blue-500 text-blue-700' 
          : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-gray-50'
      }`}
    >
      <div className="font-bold text-sm mb-0.5">{label}</div>
      <div className={`text-xs ${isSelected ? 'text-blue-500' : 'text-gray-400'}`}>{desc}</div>
      {isSelected && (
        <div className="absolute top-0 right-0 w-4 h-4 bg-blue-500 rounded-bl-lg">
          <svg className="w-3 h-3 text-white absolute top-0.5 right-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </button>
  );
};

export default PermissionModal;
